import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { cloneDeep } from 'lodash';
import { Classroom360Service } from '../../classroom360/classroom360.service';
import { SelectCourseDialogComponent } from '../../classroom360/guards/select-course/select-course-dialog/select-course-dialog.component';
import { MultipleFamiliesComponent } from '../../student360/online-registration/student-update/multiple-families/multiple-families.component';
import { Portal } from '../portal.model';
import { UserService } from '../user/user.service';
import { ClassroomSummaryService } from './../../classroom360/classroom-summary/classroom-summary.service';
import { DataQueryService } from './../../classroom360/data-query/data-query.service';
import { LoginService } from './../../shared-modules/login/login.service';
import { OnlineRegistrationService } from './../../student360/online-registration/online-registration.service';
import { SharedService } from './../shared.service';

export class PortalSelectionEvent {
    portal: Portal;
    selected: boolean;
}

@Component({
    selector: 'portal-menu',
    templateUrl: './portal-menu.component.html',
    styleUrls: ['./portal-menu.component.css']
})
export class PortalMenuComponent implements OnInit {

    // Interface Properties
    @Input() navigable = false;
    @Input() selectable = false;
    @Input() selectedPortals: { [portal: number]: boolean } = {};
    @Input() searchable = false;
    @Input() topLevel = true;
    @Input() searchValue: string;

    private _portals: Portal[] = [];
    get portals(): Portal[] {
        return this._portals;
    }
    @Input() set portals(value: Portal[]) {
        this._portals = value;
        this.displayPortals = value;
    }

    @Output() portalSelected = new EventEmitter<PortalSelectionEvent>();
    @Output() portalExpanded = new EventEmitter<Portal>();
    @Output() portalNavigated = new EventEmitter<Portal>();
    @Output() expandCollapseClick = new EventEmitter<number>();

    // Local Properties
    @ViewChild('divMenu') divMenu: ElementRef;
    displayPortals: Portal[] = [];
    search: string;
    menuClicked = false;
    userType: number;
    selectedPortalName = '';
    selectedPortalUrl = '';

    // Constructor
    constructor(
        private router: Router,
        private dataQueryService: DataQueryService,
        private dialog: MatDialog,
        private classroomSummaryService: ClassroomSummaryService,
        private sharedService: SharedService,
        private loginService: LoginService,
        private onlineRegistrationService: OnlineRegistrationService,
        private userService: UserService,
        private classroom360Service: Classroom360Service
    ) { }

    // Event Functions
    ngOnInit(): void {
        this.userType = this.userService.userType;

        this.userService.observableCurrentUserStream$.subscribe(user => {
            this.userType = this.userService.userType;
        });

        this.classroom360Service.observableStaffAssignedPortalsStream$.subscribe(portals => {
            if (this.userType == 1 && this.router.url.toLowerCase() != '/login' && this.router.url.toLowerCase() != 'classroom360/cards') {
                const selectedPortal = this.portals.filter(o => o.PortalDescription != null && o.PortalDescription != '' && this.router.url.toLowerCase().indexOf(o.ParentModule.toLowerCase() + o.PortalDescription.toLowerCase()) >= 0)[0];
                if (selectedPortal) {
                    this.selectedPortalName = selectedPortal.PortalName;
                    this.selectedPortalUrl = selectedPortal.PortalDescription;
                }
            }
        });

        this.router.events.subscribe((event) => {
            if (this.selectedPortalUrl != '') {
                if (event instanceof NavigationEnd) {
                    if (this.router.url.toLowerCase().indexOf(this.selectedPortalUrl.toLowerCase()) < 0) {
                        this.selectedPortalName = '';
                        this.selectedPortalUrl = '';
                    }
                }
            }
        });
    }

    // UI Functions
    portalClick(portal: Portal) {
        // PRE: A portal is clicked
        // POST: naviates to that page
        // TODO: We need to update the pages to have loading interceptors for the data they require
        if (this.navigable) {
            let parentModule = portal.ParentModule ? portal.ParentModule : '/student360';
            if (this.loginService.LoginSettings.V10 == false && (this.loginService.LoginSettings.UseSection504 == true || this.loginService.LoginSettings.EnableSped360 == true) && this.userType == 1 && portal.PortalName == 'Special Services' && (portal.GroupName == 'Students' || portal.GroupName == 'My Students')) {
                parentModule = '/sped360';
                portal.PortalDescription = '/studentDocumentHome';
            }
            this.sharedService.currentPortal = portal;
            this.portalNavigated.emit(portal);
            let isUHD = false;
            switch (portal.PortalName) {
                // NOTE! You should not include specific portal logic. That should be handled with guards.
                // If you need to include specific logic here, include the reason on each case

                // V9 UHD and OR has a multiple families check
                // I don't know if this can be handled via a guard because OR is a mess
                case 'Update Household Data':
                    isUHD = true;
                // eslint-disable-next-line no-fallthrough
                case 'Online Registration':
                    if (!this.loginService.LoginSettings.V10) {
                        this.onlineRegistrationService.getOnlineRegFamilies(isUHD).subscribe(data => {
                            if (data.length > 1) {
                                const dialogBox = this.dialog.open(MultipleFamiliesComponent, {
                                    panelClass: 'tyl-mat-dialog',
                                    data: { families: data, isUHD: isUHD },
                                    disableClose: true
                                });
                                const subscription = dialogBox.afterClosed().subscribe(result => {
                                    subscription.unsubscribe();
                                    if (parseInt(result, 0) > 0) {
                                        this.onlineRegistrationService.isMultipleFamiliesPopupOpened = true;
                                        this.router.navigateByUrl(parentModule + portal.PortalDescription);
                                    }
                                });
                            }
                            else if (data.length == 1) {
                                this.onlineRegistrationService.isMultipleFamiliesPopupOpened = false;
                                this.onlineRegistrationService.familyId = data[0].FamilyId;
                                this.onlineRegistrationService.parentType = data[0].ParentType;
                                this.router.navigateByUrl(parentModule + portal.PortalDescription);
                            }
                        });
                    } else {
                        this.router.navigateByUrl(parentModule + portal.PortalDescription);
                    }
                    break;

                // Roster Query: Select a course
                // TODO: we're going to remove this once we've timed the changes between Classic and Angular
                case 'Roster Query':
                    if (portal.PortalName == 'Roster Query' && portal.PortalDescription != '/rosterQuery') {
                        this.dataQueryService.rosterQuery = true;
                        const dialogBox = this.dialog.open(SelectCourseDialogComponent, {
                            disableClose: true,
                            hasBackdrop: true,
                            panelClass: 'tyl-mat-dialog'
                        });
                        const subscription = dialogBox.afterClosed().subscribe(course => {
                            subscription.unsubscribe();
                            if (course) {
                                this.classroomSummaryService.selectedCourse = course;
                                this.router.navigateByUrl(parentModule + portal.PortalDescription);
                            }
                        });
                    } else {
                        this.router.navigateByUrl(parentModule + portal.PortalDescription);
                    }
                    break;

                // Gradebook: Ensure we have access
                // TODO: we're going to remove this once we've timed the changes between Classic and Angular
                case 'Gradebook':
                case 'Report Card Grades':
                    if (
                        !portal.PortalDescription.toLowerCase().includes('/v10')
                        && !portal.PortalDescription.toLowerCase().includes('/v9')
                    ) {
                        portal.PortalDescription = portal.PortalDescription + (this.loginService.LoginSettings.V10 ? '/V10' : '/V9');
                    }
                    this.router.navigateByUrl(parentModule + portal.PortalDescription);
                    break;

                // Default: just navigate and let guards take care of it
                default:
                    this.router.navigateByUrl(parentModule + portal.PortalDescription);
                    break;
            }

            if (this.userType == 1) {
                this.selectedPortalName = portal.PortalName;
                this.selectedPortalUrl = portal.PortalDescription;
            }
        }
    }

    menuToggled(portal: Portal, expanded: boolean) {
        // PRE: A menu is expanded AFTER a user has clicked on a menu
        // POST: emits that portal click and sets the portal's expanded property
        if (this.menuClicked) {
            portal.Expanded = expanded ? 1 : 0;
            if (!this.search) {
                this.portalExpanded.emit(portal);
            }
            this.menuClicked = false;
        }
    }

    menuClick(event: Event) {
        // PRE: the menu is clicked
        // POST: sets the menu as clicked, then stops the propagation if we aren't expanding or collapsing
        this.menuClicked = true;
        event.stopPropagation();
    }

    portalCheckboxChange(portal: Portal, selected: boolean) {
        // PRE: A portal's checkbox is clicked
        // POST: toggles whether the portal is selected and emits the portal
        this.selectedPortals[portal.Id] = selected;
        this.portalSelected.emit({ portal: portal, selected: this.selectedPortals[portal.Id] });
    }

    performSearch() {
        // PRE: A search is performed by clicking on the search button or pressing enter in the field
        // POST: performs the search and limits the menu to matching portals
        if (this.search) {
            const filterPortals = (portals: Portal[]): Portal[] => {
                const filteredPortals: Portal[] = [];
                portals.forEach(portal => {
                    const filteredPortal = cloneDeep(portal);
                    const filteredChildPortals = filterPortals(filteredPortal.ChildPortals);
                    const exactMatch = filteredPortal.PortalName.trim().toLowerCase() == this.search.trim().toLowerCase();
                    const partialMatch = filteredPortal.PortalName.trim().toLowerCase().includes(this.search.trim().toLowerCase());
                    const hasChildren = filteredChildPortals.length > 0;
                    if (!exactMatch && hasChildren) {
                        filteredPortal.ChildPortals = filteredChildPortals;
                    }
                    if (partialMatch || hasChildren) {
                        filteredPortal.Expanded = 1;
                        filteredPortals.push(cloneDeep(filteredPortal));
                    }
                });
                return filteredPortals;
            };
            this.displayPortals = filterPortals(this.portals);
        } else {
            this.displayPortals = this.portals;
        }
        this.searchValue = this.search;
    }

    expandCollapseButtonClick(expandAll: number) {
        if (!this.search) {
            this.expandCollapseClick.emit(expandAll);
        }
        else {
            this.expandCollapseAll(expandAll);
        }
    }

    expandCollapseAll(expandAll: number, selectedPortal: Portal = null) {
        if (selectedPortal == null) {
            this.displayPortals.forEach(portal => {
                if (portal.ChildPortals != null && portal.ChildPortals.length > 0) {
                    portal.Expanded = expandAll;

                    portal.ChildPortals.forEach(childPortal => {
                        this.expandCollapseAll(expandAll, childPortal);
                    });
                }
            });
        }
        else if (selectedPortal != null && selectedPortal.ChildPortals != null && selectedPortal.ChildPortals.length > 0) {
            selectedPortal.Expanded = expandAll;

            selectedPortal.ChildPortals.forEach(childPortal => {
                this.expandCollapseAll(expandAll, childPortal);
            });
        }
    }

    sideNavOpened() {
        if (this.userType == 1 && this.searchable) {
            if (this.router.url.toLowerCase().indexOf('classroom360/cards') >= 0) {
                this.divMenu.nativeElement.scrollTop = 0;
            }
        }
    }
}